import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown } from "lucide-react";
import { SeverityLevel } from "@/types/security";

interface IssueSeverityData {
  critical: number;
  high: number;
  medium: number;
  low: number;
  criticalTrend: number;
  highTrend: number;
  mediumTrend: number;
  lowTrend: number;
}

interface IssueSeverityCardProps {
  data: IssueSeverityData;
  onSeverityClick: (severity: SeverityLevel) => void;
}

export function IssueSeverityCard({ data, onSeverityClick }: IssueSeverityCardProps) {
  const severityItems: Array<{
    label: string;
    value: number;
    trend: number;
    severity: SeverityLevel;
    color: string;
    bg: string;
  }> = [
    {
      label: "Critical Issues",
      value: data.critical,
      trend: data.criticalTrend,
      severity: "CRITICAL",
      color: "text-red-600",
      bg: "bg-red-50",
    },
    {
      label: "High Issues",
      value: data.high,
      trend: data.highTrend,
      severity: "HIGH",
      color: "text-orange-600",
      bg: "bg-orange-50",
    },
    {
      label: "Medium Issues",
      value: data.medium,
      trend: data.mediumTrend,
      severity: "MEDIUM",
      color: "text-yellow-600",
      bg: "bg-yellow-50",
    },
    {
      label: "Low Issues",
      value: data.low,
      trend: data.lowTrend,
      severity: "LOW",
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium">Issue Severity</CardTitle>
        <p className="text-sm text-muted-foreground">Current versus a month ago</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {severityItems.map((item) => (
          <div
            key={item.severity}
            className="flex items-center justify-between p-3 rounded-lg hover:bg-accent cursor-pointer transition-colors"
            onClick={() => onSeverityClick(item.severity)}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-9 h-9 rounded flex items-center justify-center ${item.bg} ${item.color} font-bold text-base flex-shrink-0`}
              >
                {item.severity.charAt(0)}
              </div>
              <div className="h-9 flex items-center">
                <p className="text-2xl font-bold leading-none">{item.value}</p>
              </div>
              <p className="text-sm text-muted-foreground leading-none">{item.label}</p>
            </div>
            {Math.abs(item.trend) > 0 && (
              <div
                className={`flex items-center gap-1 ${item.trend >= 0 ? "text-red-600" : "text-green-600"}`}
              >
                {item.trend >= 0 ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                <span className="text-xs font-medium">{Math.abs(item.trend)}%</span>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
